import type {
  PurchaseFreezeResult,
  StreakHistory,
  StreakHistoryParams,
  StreakLeaderboard,
  StreakStatus,
} from '@/types/streak.types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

/**
 * Streak API Service
 * Handles the tiered daily-volume streak: status, points/freeze history, and
 * spending points on a streak freeze.
 */
export const streakApi = {
  getStatus: async (): Promise<StreakStatus> => {
    const { data } = await axiosClient.get<{ success: boolean; data: StreakStatus }>(
      API_ENDPOINTS.STREAK.STATUS,
    );
    return data.data;
  },

  getHistory: async (params: StreakHistoryParams = {}): Promise<StreakHistory> => {
    const { data } = await axiosClient.get<{ success: boolean; data: StreakHistory }>(
      API_ENDPOINTS.STREAK.HISTORY,
      { params },
    );
    return data.data;
  },

  getLeaderboard: async (): Promise<StreakLeaderboard> => {
    const { data } = await axiosClient.get<{ success: boolean; data: StreakLeaderboard }>(
      API_ENDPOINTS.STREAK.LEADERBOARD,
    );
    return data.data;
  },

  purchaseFreeze: async (): Promise<PurchaseFreezeResult> => {
    try {
      const { data } = await axiosClient.post<{
        success: boolean;
        message?: string;
        data: PurchaseFreezeResult;
      }>(API_ENDPOINTS.STREAK.PURCHASE_FREEZE);
      if (data && data.success === false) {
        throw new Error(data.message || 'Failed to purchase streak freeze');
      }
      return data.data;
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      throw new Error(backendMessage || err?.message || 'Failed to purchase streak freeze');
    }
  },

  dismissBanner: async (): Promise<{ bannerDismissed: boolean }> => {
    const { data } = await axiosClient.post<{
      success: boolean;
      data: { bannerDismissed: boolean };
    }>(API_ENDPOINTS.STREAK.DISMISS_BANNER);
    return data.data;
  },
};
